import React from 'react';
import {
  Box,
  Flex,
  Text,
  Icon,
  useColorModeValue,
  Tooltip
} from '@chakra-ui/react';

/**
 * MetricCard - A modern, visually appealing metric display component
 * 
 * @param {Object} props
 * @param {React.ComponentType} props.icon - Icon component from react-icons
 * @param {string} props.title - The metric label/title
 * @param {string|number} props.value - The main metric value
 * @param {string} props.description - Supporting description text
 * @param {string} props.colorScheme - Chakra UI color scheme (blue, purple, teal, green, orange, etc.)
 * @param {number} props.index - Optional index for staggered animations
 * @param {string} props.tooltip - Optional tooltip text for additional context
 */
const MetricCard = ({
  icon,
  title,
  value,
  description,
  colorScheme = 'blue',
  index = 0,
  tooltip
}) => {
  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const iconRingBg = useColorModeValue(`${colorScheme}.100`, `${colorScheme}.800`);
  const titleColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.900', 'white');
  const descriptionColor = useColorModeValue('gray.500', 'gray.500');

  // Gradient colors for the accent bar
  const gradientColors = {
    blue: 'linear(to-r, blue.400, blue.600)',
    purple: 'linear(to-r, purple.400, purple.600)',
    teal: 'linear(to-r, teal.400, teal.600)',
    green: 'linear(to-r, green.400, green.600)',
    orange: 'linear(to-r, orange.400, orange.600)',
    cyan: 'linear(to-r, cyan.400, cyan.600)',
    pink: 'linear(to-r, pink.400, pink.600)',
    red: 'linear(to-r, red.400, red.600)'
  };

  const CardContent = (
    <Box
      bg={cardBg}
      borderRadius="xl"
      overflow="hidden"
      position="relative"
      boxShadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        transform: 'translateY(-4px)',
        boxShadow: 'lg',
        borderColor: `${colorScheme}.200`
      }}
      role="group"
    >
      {/* Top gradient accent bar */}
      <Box
        h="3px"
        bgGradient={gradientColors[colorScheme] || gradientColors.blue}
      />

      <Flex
        direction="column"
        p={5}
        gap={3}
      >
        {/* Icon and Title Row */}
        <Flex
          align="center"
          gap={3}
        >
          {/* Icon with layered circle background */}
          <Box position="relative">
            {/* Outer ring */}
            <Box
              position="absolute"
              top="-2px"
              left="-2px"
              w="44px"
              h="44px"
              borderRadius="full"
              bg={iconRingBg}
              opacity={0.5}
            />
            {/* Inner circle with icon */}
            <Flex
              w="40px"
              h="40px"
              borderRadius="full"
              bg={iconBg}
              align="center"
              justify="center"
              position="relative"
              zIndex={1}
            >
              <Icon
                as={icon}
                boxSize={5}
                color={`${colorScheme}.500`}
              />
            </Flex>
          </Box>

          {/* Title */}
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={titleColor}
            letterSpacing="wide"
            textTransform="uppercase"
          >
            {title}
          </Text>
        </Flex>

        {/* Value - Large and prominent */}
        <Text
          fontSize="3xl"
          fontWeight="bold"
          color={valueColor}
          lineHeight="1.1"
          letterSpacing="-0.02em"
        >
          {value}
        </Text>

        {/* Description with subtle left border accent */}
        <Flex
          align="center"
          gap={2}
        >
          <Box
            w="2px"
            h="14px"
            borderRadius="full"
            bg={`${colorScheme}.400`}
            opacity={0.6}
          />
          <Text
            fontSize="sm"
            color={descriptionColor}
          >
            {description}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );

  if (tooltip) {
    return (
      <Tooltip
        label={tooltip}
        placement="top"
        hasArrow
        bg={`${colorScheme}.600`}
        color="white"
        px={3}
        py={2}
        borderRadius="md"
      >
        {CardContent}
      </Tooltip>
    );
  }

  return CardContent;
};

/**
 * MetricCardCompact - A compact variant for tighter layouts
 */
export const MetricCardCompact = ({
  icon,
  title,
  value,
  description,
  colorScheme = 'blue'
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const titleColor = useColorModeValue('gray.500', 'gray.400');
  const valueColor = useColorModeValue('gray.900', 'white');
  const descriptionColor = useColorModeValue('gray.500', 'gray.500');

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      p={4}
      borderWidth="1px"
      borderColor={borderColor}
      transition="all 0.2s"
      _hover={{
        borderColor: `${colorScheme}.200`,
        boxShadow: 'sm'
      }}
    >
      <Flex align="center" gap={3}>
        <Flex
          w="36px"
          h="36px"
          borderRadius="lg"
          bg={iconBg}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon
            as={icon}
            boxSize={4}
            color={`${colorScheme}.500`}
          />
        </Flex>
        <Box flex="1" minW={0}>
          <Text
            fontSize="xs"
            color={titleColor}
            fontWeight="medium"
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {title}
          </Text>
          <Flex align="baseline" gap={2}>
            <Text
              fontSize="xl"
              fontWeight="bold"
              color={valueColor}
              lineHeight="1.2"
            >
              {value}
            </Text>
            {description && (
              <Text
                fontSize="xs"
                color={descriptionColor}
                noOfLines={1}
              >
                {description}
              </Text>
            )}
          </Flex>
        </Box>
      </Flex>
    </Box>
  );
};

/**
 * MetricCardLarge - A larger variant with more visual prominence
 */
export const MetricCardLarge = ({
  icon,
  title,
  value,
  description,
  colorScheme = 'blue',
  trend,
  trendLabel
}) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.50`, `${colorScheme}.900`);
  const titleColor = useColorModeValue('gray.600', 'gray.400');
  const valueColor = useColorModeValue('gray.900', 'white');
  const descriptionColor = useColorModeValue('gray.500', 'gray.500');

  const gradientColors = {
    blue: 'linear(to-br, blue.400, blue.600)',
    purple: 'linear(to-br, purple.400, purple.600)',
    teal: 'linear(to-br, teal.400, teal.600)',
    green: 'linear(to-br, green.400, green.600)',
    orange: 'linear(to-br, orange.400, orange.600)',
    cyan: 'linear(to-br, cyan.400, cyan.600)',
    pink: 'linear(to-br, pink.400, pink.600)',
    red: 'linear(to-br, red.400, red.600)'
  };

  return (
    <Box
      bg={cardBg}
      borderRadius="2xl"
      overflow="hidden"
      position="relative"
      boxShadow="md"
      borderWidth="1px"
      borderColor={borderColor}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      _hover={{
        transform: 'translateY(-6px)',
        boxShadow: 'xl'
      }}
    >
      {/* Gradient accent bar */}
      <Box
        h="4px"
        bgGradient={gradientColors[colorScheme] || gradientColors.blue}
      />

      <Flex
        direction="column"
        p={6}
        gap={4}
      >
        {/* Header with icon */}
        <Flex justify="space-between" align="flex-start">
          <Box>
            <Text
              fontSize="sm"
              fontWeight="semibold"
              color={titleColor}
              letterSpacing="wide"
              textTransform="uppercase"
              mb={2}
            >
              {title}
            </Text>
            <Text
              fontSize="4xl"
              fontWeight="bold"
              color={valueColor}
              lineHeight="1"
              letterSpacing="-0.03em"
            >
              {value}
            </Text>
          </Box>
          <Flex
            w="56px"
            h="56px"
            borderRadius="xl"
            bg={iconBg}
            align="center"
            justify="center"
            boxShadow="sm"
          >
            <Icon
              as={icon}
              boxSize={7}
              color={`${colorScheme}.500`}
            />
          </Flex>
        </Flex>

        {/* Description and optional trend */}
        <Flex
          justify="space-between"
          align="center"
          pt={2}
          borderTopWidth="1px"
          borderTopColor={borderColor}
        >
          <Text
            fontSize="sm"
            color={descriptionColor}
          >
            {description}
          </Text>
          {trend !== undefined && (
            <Flex
              align="center"
              gap={1}
              px={2}
              py={1}
              borderRadius="full"
              bg={trend >= 0 ? 'green.50' : 'red.50'}
            >
              <Text
                fontSize="sm"
                fontWeight="semibold"
                color={trend >= 0 ? 'green.600' : 'red.600'}
              >
                {trend >= 0 ? '+' : ''}{trend}%
              </Text>
              {trendLabel && (
                <Text
                  fontSize="xs"
                  color={trend >= 0 ? 'green.500' : 'red.500'}
                >
                  {trendLabel}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

export default MetricCard;
